import type { ComponentProps } from 'react'
import { CaretLeft, CaretRight, DotsThree } from '@/components/icons'
import { buttonVariants, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/cn'

/**
 * shadcn Pagination. Links are rendered as buttons because the page index
 * lives in the URL search params and is written through the route's own
 * `update()`; an `<a href>` would bypass that contract.
 */
export function Pagination({ className, ...props }: ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

export function PaginationContent({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn('flex flex-row items-center gap-1', className)}
      {...props}
    />
  )
}

export function PaginationItem({ ...props }: ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, 'size'> &
  ComponentProps<'button'>

export function PaginationLink({
  className,
  isActive,
  size = 'icon-sm',
  ...props
}: PaginationLinkProps) {
  return (
    <button
      type="button"
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({ variant: isActive ? 'outline' : 'ghost', size }),
        'tabular-nums',
        isActive && 'bg-[color:var(--card)] font-semibold shadow-[var(--shadow-raised)]',
        className,
      )}
      {...props}
    />
  )
}

export function PaginationPrevious({ className, ...props }: ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Previous page"
      size="sm"
      className={cn('gap-1 pl-2', className)}
      {...props}
    >
      <CaretLeft className="size-3.5" />
      <span className="hidden sm:inline">Previous</span>
    </PaginationLink>
  )
}

export function PaginationNext({ className, ...props }: ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Next page"
      size="sm"
      className={cn('gap-1 pr-2', className)}
      {...props}
    >
      <span className="hidden sm:inline">Next</span>
      <CaretRight className="size-3.5" />
    </PaginationLink>
  )
}

export function PaginationEllipsis({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn(
        'flex size-7 items-center justify-center text-[color:var(--muted-foreground)]',
        className,
      )}
      {...props}
    >
      <DotsThree className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  )
}
